import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, TextInput, Checkbox, InlineNotification } from '@carbon/react';
import { ArrowRight, View, ViewOff } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import OAuthButtons from '../components/OAuthButtons';
import logo from '../assets/logo.webp';
import promoLogo from '../assets/logo-removebg-preview.webp';
import './AuthPages.scss';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { signIn, signInWithGoogle, signInWithApple, isAuthenticated, loading: authLoading, error: authError } = useAuth();
  
  // Get the page the user was trying to access before being redirected to login
  const from = location.state?.from?.pathname || '/dashboard';
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  const isDark = theme === 'dark';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }
    
    const { data, error: signInError } = await signIn(formData.email, formData.password);
    
    if (signInError) {
      setError(signInError.message || 'Invalid email or password');
      setLoading(false);
      return;
    }
    
    if (data) {
      navigate(from, { replace: true });
    }
    
    setLoading(false);
  };

  // OAuth handlers - simplified to match sensorpushweb pattern
  const handleGoogleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      await signInWithGoogle();
      // OAuth will redirect, so we don't need to handle success here
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
      setOauthLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      await signInWithApple();
      // OAuth will redirect, so we don't need to handle success here
    } catch (err) {
      setError(err.message || 'Failed to sign in with Apple');
      setOauthLoading(false);
    }
  };

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="login-page">
      <div className="auth-page__container auth-page__container--split">
        {/* Left Side - Promotional Section */}
        <div className="auth-page__promo">
          <div className="auth-page__promo-content">
            <img src={promoLogo} alt="ChillVerify Logo" style={{ width: '80px', height: 'auto', marginBottom: '20px' }} />
            <h1 className="auth-page__promo-title">Welcome to</h1>
            <h2 className="auth-page__promo-brand">ChillVerify</h2>
            <p className="auth-page__promo-subtitle">
              Monitor Temperature & Humidity with Ease
            </p>
            <p className="auth-page__promo-body">
              Advanced IoT sensor solutions for comprehensive environmental monitoring and real-time data analytics.
            </p>
            <button 
              className="auth-page__promo-link"
              onClick={() => navigate('/')}
            >
              Learn more
            </button>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="auth-page__form-section">
          <div className="auth-card">
          <div className="auth-card__header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' }}>
              <img 
                src={logo} 
                alt="ChillVerify Logo" 
                style={{ width: '36px', height: '36px', borderRadius: '6px' }}
              />
              <h2 className="auth-card__title auth-card__title--brand" style={{ margin: 0 }}>ChillVerify</h2>
            </div>
            <p className="auth-card__subtitle">Sign in to your account</p>
          </div>

          {(error || authError) && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error || authError}
              onClose={() => setError('')}
              className="auth-card__notification"
            />
          )}

          <form onSubmit={handleSubmit} className="auth-card__form">
            <div className="auth-card__fields">
              <TextInput
                id="email"
                name="email"
                type="email"
                labelText="Email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
              />
              <div className="auth-card__password-field">
                <TextInput
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  labelText="Password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="auth-card__password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <ViewOff size={18} /> : <View size={18} />}
                </button>
              </div>
            </div>

            <div className="auth-card__options">
              <Checkbox
                id="remember"
                labelText="Remember me"
                checked={rememberMe}
                onChange={(_, { checked }) => setRememberMe(checked)}
              />
              <span onClick={() => navigate('/forgot-password')} className="auth-card__link">
                Forgot password?
              </span>
            </div>

            <div className="auth-card__actions">
              <Button kind="primary" type="submit" className="auth-card__btn" disabled={loading || authLoading} renderIcon={ArrowRight}>
                {loading || authLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </div>
          </form>

          {/* OAuth Login */}
          <OAuthButtons
            onGoogleSignIn={handleGoogleSignIn}
            onAppleSignIn={handleAppleSignIn}
            isLoading={oauthLoading || loading || authLoading}
            mode="signin"
            showApple={true}
          />

          <p className="auth-card__footer">
            Don't have an account?{' '}
            <span onClick={() => navigate('/signup')} className="auth-card__link">Sign up</span>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
